import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/router'

import { SocialSignInButton, SignInTargetType } from '@/shared/domains/oauth'
import { postAmplitude } from '@/core/lib'
import { generateTagId } from '@/utils/test-automation'
import { cn } from '@/shared/utils'
import EmailIcon from '@/components/atomic/templates/SignInPageTemplate/svgs/email.svg'
import { SocialSignInTargets } from '@/components/atomic/templates/SignInPageTemplate'
import { useLocales } from '@/shared/hooks/useLocales'

const SignInButtons: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { locale } = useLocales()
  const router = useRouter()

  const handleSignInButtonClick = (target: SignInTargetType) => {
    // 기존 이벤트 유지
    postAmplitude(`click_login_modal_via_${target}`, { screen: `열람제한` })

    // 새로운 통합 이벤트 추가
    postAmplitude('click_login', {
      provider: target,
      source_type: 'paywall',
      page_path: router.pathname,
      language: i18n.language,
    })
  }

  return (
    <div className={cn('px-6 py-12')} data-testid="sign-in-buttons">
      {/* 제목 */}
      <div className={cn('flex justify-center mb-9')}>
        <h2
          className={cn('text-center text-xl font-semibold text-black')}
          id={generateTagId('login', 'section', 'text', 'title', 'loginNudge')}
        >
          {t('webLoginCalloutTitle')}
        </h2>
      </div>

      {/* 툴팁 */}
      <div className={cn('text-center mb-6')}>
        <span
          className={cn(
            'relative inline-block mb-px px-3.5 py-1.5',
            'border border-orange-500 rounded-2xl bg-white text-orange-500 text-sm font-medium',
            'shadow-sm',
            "after:content-[''] after:absolute after:-bottom-2.5 after:left-1/2 after:-translate-x-1/2",
            "after:w-9 after:h-2.5 after:bg-[url('/assets/icon/orange-down-arrow.svg')] after:bg-no-repeat",
          )}
        >
          {t('webLoginCalloutNudgeTooltip')}
        </span>
      </div>

      {/* 메인 로그인 버튼들 */}
      <section className={cn('flex flex-col gap-3 p-6 mb-6', 'border border-gray-300 rounded-lg bg-white')}>
        {SocialSignInTargets[locale].map((target) => (
          <div key={target} className={cn('h-12')}>
            <SocialSignInButton
              text={t(`pages.communityMedicalTipDetail.SocialSignInBox.${target}Button`)}
              target={target}
              align="center"
              onClick={handleSignInButtonClick}
            />
          </div>
        ))}
      </section>

      {/* 구분선 */}
      <div className={cn('flex justify-center items-center gap-2.5 mt-6 mb-6')}>
        <span
          className={cn(
            'relative text-gray-600 text-sm',
            "before:content-[''] before:absolute before:-top-1.5 before:-left-24 before:w-20 before:h-px before:bg-gray-400",
            "after:content-[''] after:absolute after:-top-1.5 after:-right-24 after:w-20 after:h-px after:bg-gray-400",
          )}
        >
          {t('pages.communityMedicalTipDetail.SocialSignInBox.or')}
        </span>
      </div>

      {/* 기타 로그인 옵션 */}
      <div className={cn('flex justify-center gap-7')}>
        <SocialSignInButton target="facebook" onlyIcon onClick={handleSignInButtonClick} />
        <button
          id={generateTagId('login', 'section', 'btn', 'login', 'email')}
          onClick={() => {
            handleSignInButtonClick('email')
            router.push({
              pathname: '/signin/email',
              query: {
                return_url: router.asPath,
              },
            })
          }}
        >
          <EmailIcon />
        </button>
      </div>
    </div>
  )
}

export default SignInButtons
